// Lightweight A/B test utilities.
//
// Assignment is deterministic: hash(experimentName + GA client_id) → an index
// into the experiment's declared variant labels, split evenly. The decision is
// mirrored to a cookie (90 days) so it survives even if the GA cookie resets.
//
// Each experiment declares its own variant names. The invariant is positional:
// `variants[0]` is the baseline (no change), the rest are treatments. The labels
// themselves are free-form — pick what reads best in GA4 reports.
//
// Assignment is lazy: a variant is only stored when getABVariant() is first
// called at the point of UI divergence (e.g. inside the Stripe checkout
// flow). Users who never reach that point never enter the experiment — this
// keeps the cohort clean of legacy traffic.
//
// The variant is communicated to analytics by attaching it as an event
// parameter on the funnel events (exposure + conversion), not as a global
// user_property. See gtmStripeRedirectStarted / gtmPaymentCompleted.
//
// A signed-in account keeps its arms on the server (User.ab_arms, keyed by the cookie
// name), so every browser it signs in from is in the same arm: /users/me's arms go over
// the cookies (adoptAccountArms), and an arm rolled while signed in goes to the account
// (keepRolledArms) — the first browser to roll one decides it, unless a tester's `?ab_…=` link
// forces another (pendingLinkArms). An `enforced` experiment
// changes what the backend enforces (a quota): its arm is taken at signup only (signupArms
// → getAuthParams), and only from the labels the backend lists.

import {getClientIdFromCookie, paramsGet, paramsRemove} from 'utils';
import {FEATURES, isFeatureEnabled} from 'config';
import type {PlanPeriod} from 'types';
import {safeStorage} from 'lib/safeStorage';

/** [baseline, ...treatments] — order matters: variants[0] is the no-change group. */
type Variants = readonly [string, ...string[]];

interface ExperimentConfig {
    readonly cookie: string;
    readonly variants: Variants;
    /** The backend enforces the arm (a quota) and lists the labels itself; it takes one at signup only. */
    readonly enforced?: boolean;
    /** Retired labels still found on cookies, accounts and links → the variant now in their place. */
    readonly aliases?: Readonly<Record<string, string>>;
}

/**
 * Registry of active experiments. Add a new one by appending an entry.
 *
 * `paywall` (Figma 468:26078) — what a limit wall sells: `b` the big paywall alone,
 * `a2` the big paywall with the one-time scan beside the plan, `e` a compact popup
 * that leads to `b`'s plan cards, `d` a compact popup that leads to the choice
 * between the plan and the one-time scan. `e` took over `c`'s bucket, so an arm kept as
 * `c` reads as `e`.
 *
 * `humanizerQuota` — the Free plan's Humanizer grant, in words. The words come from the
 * backend (/payments/prices `experiments`); a label it doesn't list grants nothing.
 */
export const AB_EXPERIMENTS = {
    trial: {cookie: 'ab_trial_v1', variants: ['control', 'trial']} as const,
    paywall: {cookie: 'ab_paywall_v2', variants: ['b', 'a2', 'e', 'd'], aliases: {c: 'e'}} as const,
    humanizerQuota: {cookie: 'ab_humanizer_quota_v1', variants: ['500', '800'], enforced: true} as const,
    // Existing popup vs no popup; enrolled only when an eligible announcement could open.
    humanizerAnnouncement: {cookie: 'ab_humanizer_announcement_v1', variants: ['show', 'hide']} as const,
} as const satisfies Record<string, ExperimentConfig>;

export type ABExperimentKey = keyof typeof AB_EXPERIMENTS;

/** Variant labels for a given experiment, narrowed via const inference. */
export type ABVariant<K extends ABExperimentKey = ABExperimentKey> =
    (typeof AB_EXPERIMENTS)[K]['variants'][number];

const COOKIE_MAX_AGE_DAYS = 90;

/** Enforced tests a signup can still be put in (/payments/prices): cookie → {arm: words}. */
export type ServerArms = Record<string, Record<string, number>>;

/** Arms by cookie name, as the account keeps them (User.ab_arms). */
export type ABArms = Record<string, string>;

const EXPERIMENTS = AB_EXPERIMENTS as Record<string, ExperimentConfig>;

/** Where an arm rolled while an account is signed in goes (userStore); null while anonymous. */
let keepOnAccount: ((arms: ABArms) => void) | null = null;

/** Returns the stored or freshly assigned variant for an experiment. */
export function getABVariant<K extends ABExperimentKey>(key: K): ABVariant<K> {
    return assign(AB_EXPERIMENTS[key]) as ABVariant<K>;
}

function assign(config: ExperimentConfig): string {
    const existing = readArm(config);
    if (existing) return existing;

    const {cookie, variants} = config;
    const clientId = getClientIdFromCookie();
    const variant = variants[hashIndex(`${cookie}_${clientId ?? Math.random()}`, variants.length)];
    writeArm(config, variant);
    // An enforced arm is a signup's to set, never a later roll's.
    if (!config.enforced) keepOnAccount?.({[cookie]: variant});
    return variant;
}

/** The declared variant `label` names, directly or through an alias. */
function resolveArm({variants, aliases}: ExperimentConfig, label: string | null | undefined): string | undefined {
    const variant = label ? aliases?.[label] ?? label : undefined;
    return variant && variants.includes(variant) ? variant : undefined;
}

/** The arm the cookie holds, if the experiment declares it; an aliased cookie is rewritten to it. */
function readArm(config: ExperimentConfig): string | undefined {
    const stored = getCookie(config.cookie);
    const arm = resolveArm(config, stored);
    if (arm && arm !== stored) writeArm(config, arm);
    return arm;
}

/** Writes the variant `label` names and returns it; undefined, writing nothing, if the experiment doesn't declare it. */
function writeArm(config: ExperimentConfig, label: string | null | undefined): string | undefined {
    const variant = resolveArm(config, label);
    if (variant) setCookie(config.cookie, variant, COOKIE_MAX_AGE_DAYS);
    return variant;
}

/**
 * The arms an auth request names, `{cookie: arm}`, for every enforced test. Signup is the
 * last moment such an arm can be assigned, so a visitor who never met the experiment is
 * rolled here — the cookie is lazy, the account is not. Named whether or not the test
 * enrols: the Google callback asks before /payments/prices has loaded, and the backend is
 * what decides (a new account in an enrolling test only, a listed label only).
 */
export function signupArms(): ABArms {
    const named: ABArms = {};
    for (const config of Object.values(EXPERIMENTS)) {
        if (config.enforced) named[config.cookie] = assign(config);
    }
    return named;
}

/**
 * A signed-in account's arms are the server's, so the cookies follow them. An enforced test
 * the account is in no arm of leaves this browser, so an arm rolled as an anon before signing
 * into an older account never labels its events. Returns the arms this browser rolled in
 * tests the account has no arm for yet, for the account to keep.
 */
export function adoptAccountArms(arms: ABArms | null | undefined): ABArms {
    const unkept: ABArms = {};
    for (const config of Object.values(EXPERIMENTS)) {
        const kept = arms?.[config.cookie];
        if (writeArm(config, kept)) continue;
        if (config.enforced) deleteCookie(config.cookie);
        else if (kept === undefined) {
            const own = readArm(config);
            if (own) unkept[config.cookie] = own;
        }
    }
    return unkept;
}

/** Send every arm rolled from now on to `keep` (null stops it): userStore, while signed in. */
export function keepRolledArms(keep: ((arms: ABArms) => void) | null): void {
    keepOnAccount = keep;
}

/**
 * Put this browser in `variant` of experiment `key`; null forgets the arm, so the next
 * divergence assigns it again (by the GA client id). Testing aid (adminUtil's setAB): only
 * declared variants.
 */
export function setABVariant(key: string, variant: string | null): void {
    const config = getABExperiment(key, variant);
    if (variant === null) deleteCookie(config.cookie);
    else writeArm(config, variant);
    // The last pick wins: an older link's arm must not be forced onto the account at sign-in.
    clearLinkArms([config.cookie]);
}

/**
 * An experiment by its AB_EXPERIMENTS key; throws, naming the known ones, on any other — and on
 * a `variant` it doesn't declare (null names none).
 */
export function getABExperiment(key: string, variant: string | null = null): ExperimentConfig {
    const config = EXPERIMENTS[key];
    if (!config) throw new Error(`no experiment ${key} — one of ${Object.keys(EXPERIMENTS).join(', ')}`);
    if (variant !== null && !config.variants.includes(variant)) {
        throw new Error(`${key} has no variant ${variant} — one of ${config.variants.join(', ')}`);
    }
    return config;
}

/** Every experiment with its variants and this browser's arm (none = not enrolled yet). */
export function listABExperiments() {
    return Object.entries(EXPERIMENTS).map(([key, config]) => ({
        key, variants: config.variants.join(' | '), current: readArm(config) ?? '—',
        enforced: config.enforced ? 'yes' : '—', cookie: config.cookie,
    }));
}

/**
 * Puts the visitor in the arm a link names: `?ab_paywall_v2=d` writes that experiment's cookie, for
 * any experiment in AB_EXPERIMENTS, and the parameter leaves the URL. An unknown variant is ignored.
 * A front-only arm also waits for the account (pendingLinkArms) — a tester's link moves the account
 * over the arm it keeps; an enforced one only ever reaches a signup. Only where
 * FEATURES.AB_URL_OVERRIDE is on (stands, not prod). Runs before anything reads a cookie.
 */
export function applyABOverridesFromUrl(): void {
    if (!isFeatureEnabled(FEATURES.AB_URL_OVERRIDE)) return;
    const experiments = Object.values(EXPERIMENTS);
    const linked: ABArms = {};
    for (const config of experiments) {
        const arm = paramsGet(config.cookie);
        const written = writeArm(config, arm);
        if (written && !config.enforced) linked[config.cookie] = written;
    }
    paramsRemove(...experiments.map(({cookie}) => cookie));
    // Session storage, not memory: a Google sign-in leaves the page before an account exists.
    if (Object.keys(linked).length) {
        writeLinkArms({...pendingLinkArms(), ...linked});
    }
}

const LINK_ARMS_KEY = 'ab_link_arms';

/** The front-only arms a link named and no account has been moved to yet. */
export function pendingLinkArms(): ABArms {
    try {
        return JSON.parse(safeStorage(() => sessionStorage.getItem(LINK_ARMS_KEY)) ?? '{}') ?? {};
    } catch {
        return {};
    }
}

/** Forget the pending link arms of `cookies`, once the account has them (or a later pick won). */
export function clearLinkArms(cookies: string[]): void {
    const arms = pendingLinkArms();
    cookies.forEach((cookie) => delete arms[cookie]);
    writeLinkArms(arms);
}

function writeLinkArms(arms: ABArms): void {
    safeStorage(() => Object.keys(arms).length
        ? sessionStorage.setItem(LINK_ARMS_KEY, JSON.stringify(arms))
        : sessionStorage.removeItem(LINK_ARMS_KEY));
}

/** A link (on this origin, or `base`) that puts whoever opens it in `variant` of experiment `key`. */
export function buildABLink(key: string, variant: string, base: string = window.location.href): string {
    const {cookie} = getABExperiment(key, variant);
    const url = new URL(base, window.location.origin);
    url.searchParams.set(cookie, variant);
    return url.toString();
}

// --- experiment-specific semantic helpers ----------------------------------

/**
 * Is this visitor in the treatment arm? The arm comes from the cookie and from
 * nothing else — the two arms ARE the two first-timer offers, so an account that
 * has no trial left is still in the arm it was assigned to, just out of offers.
 * Gating the arm on eligibility is what once let such an account fall through to
 * the control arm and start the other offer's clock.
 *
 * Calling this writes the exposure cookie and lazily assigns the variant, so it
 * belongs on surfaces that really do diverge. Not exported: what the arm means
 * for a given surface is showsTrialOffer / showsDiscountOffer, and reading the
 * arm raw is how the two halves of one rule drift apart.
 */
function isInTrialGroup(): boolean {
    return getABVariant('trial') === 'trial';
}

/**
 * Whether `plan` is a trial-eligible plan. The 7-day self-serve trial is a
 * Premium-only offer — plus/pro/enterprise and any other tier never trial.
 * The backend enforces the same rule in is_trial_eligible.
 */
function isTrialPlan(plan: string | null | undefined): plan is 'premium' {
    return plan === 'premium';
}

/**
 * Server-side trial eligibility flag from /users/me. Independent of A/B.
 * The trial is a first-timer offer, so false means the account has already used
 * its one-time trial *or* has paid us before — a cancelled subscription counts,
 * and so does a one-off purchase. The backend decides all of it in
 * account_trial_eligible (over payments.helpers.ever_paid, the same predicate
 * behind the first-limit discount), and this is the only place the client reads
 * that decision from.
 * An anon reports eligible: their identity is not known yet, so the offer stands
 * and the rule is re-read against the account they sign in with.
 * Defaults to false when the field is missing (e.g. stale cache) — safer
 * to under-offer than to promise a trial the server will refuse.
 */
function isTrialEligible(user: {trial_eligible?: boolean} | null | undefined): boolean {
    return Boolean(user?.trial_eligible);
}

/**
 * Is the trial what this visitor is being sold on `plan`? The whole rule in one
 * place, because it is asked by every surface that offers it — the plans grid,
 * the paywall, the authenticated checkout — and the halves have drifted apart
 * before ("Start free trial" opening a full-price checkout).
 *
 * All three have to hold: the arm has to be this visitor's, the account has to
 * still have a trial left (the server's call — one per account, ever), and the
 * plan has to be the Premium the trial exists for.
 *
 * The arm is asked last, and only last: isInTrialGroup writes the exposure cookie,
 * so a plan or an account that was never going to be shown a trial must not enrol
 * into the experiment on its way past.
 *
 * False does NOT mean "show the promo instead" — which arm holds the promo is
 * showsDiscountOffer's answer, and only the cookie decides it.
 */
export function showsTrialOffer(
    user: {trial_eligible?: boolean} | null | undefined, plan: string | null | undefined,
): boolean {
    return isFeatureEnabled(FEATURES.TRIAL) && isTrialPlan(plan) && isTrialEligible(user) && isInTrialGroup();
}

export type PaywallArm = ABVariant<'paywall'>;

/**
 * This visitor's limit wall. Asking enrols them (it writes the cookie), so it belongs only
 * where a wall actually opens. Anons never get here: their wall is the sign-in form.
 */
export function paywallArm(): PaywallArm {
    return getABVariant('paywall');
}

/** Does this arm sell the one-time scan beside the plan? */
export function sellsOneTimeScan(arm: PaywallArm): boolean {
    return isFeatureEnabled(FEATURES.ONE_TIME_SCAN) && (arm === 'a2' || arm === 'd');
}

/** Does this arm open on the compact popup rather than the big paywall? */
export function opensCompactPaywall(arm: PaywallArm): boolean {
    return arm === 'e' || arm === 'd';
}

/**
 * The free Humanizer words a sign-up surface quotes: this visitor's arm while the backend
 * enrols the test, the plan's otherwise. Asking enrols the visitor, so only where it is shown.
 */
export function quotedHumanizerWords(enrolling: ServerArms, planWords: number): number {
    // Optional chaining keeps getABVariant (which enrols) off while the test isn't enrolling.
    return enrolling[AB_EXPERIMENTS.humanizerQuota.cookie]?.[getABVariant('humanizerQuota')] ?? planWords;
}

/**
 * Is the timed 40% annual discount this visitor's offer at all?
 *
 * While the trial is on, the promo is its control arm's offer; while it is off, nobody's.
 *
 * The arms of the `trial` experiment ARE the two offers, so the promo belongs to
 * the control arm and to nothing else — read from the cookie, never from what
 * the account is still entitled to. Somebody in the trial arm who has already
 * spent their one trial is therefore sold neither offer: full price, no timer.
 * Falling back to the promo there would hand one account both offers over its
 * lifetime and would book a treatment-arm miss as a control-arm impression.
 *
 * Says nothing about whether a promo window is actually open — that is the
 * server's (`discount_available_until`). The two are read together in exactly
 * one place, userStore.getDiscountRemainingMs, which every surface asks.
 *
 * The split lives on the client on purpose: the cohort is the front's (the
 * backend keeps the arm but never acts on it), so a server-side "trial-eligible
 * ⇒ no promo" would take the discount from the control arm too. What the server does guarantee is that no
 * single checkout gets both (payments.helpers.checkout_price_name).
 */
export function showsDiscountOffer(): boolean {
    // With the trial off no arm sells the promo: the 40% annual offer is withdrawn, and the
    // window (discount_available_until) stays in place for the next offer to use.
    if (!isFeatureEnabled(FEATURES.TRIAL)) return false;
    return !isInTrialGroup();
}

/**
 * The billing period to preselect in a pricing UI, by checkout context. Trial
 * offers preselect monthly (lower up-front commitment); every other context
 * (inline paid checkout, upgrade, subscription page, permission-guard modal)
 * preselects yearly. The user can still switch periods afterward.
 *
 * `callSite` names where the default is being resolved (e.g. 'paywall',
 * 'subscription_page'). It is reserved for future per-surface defaults and is
 * intentionally unused today — every caller still passes it so the wiring is in
 * place and each call is self-documenting.
 */
export function getDefaultBillingPeriod(callSite: string, opts?: {trial?: boolean}): PlanPeriod {
    // callSite is reserved for future per-surface logic (see doc comment); the
    // current rule ignores it.
    return opts?.trial ? 'monthly' : 'yearly';
}

// --- internals -------------------------------------------------------------

function hashIndex(seed: string, buckets: number): number {
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
        hash = ((hash << 5) - hash) + seed.charCodeAt(i);
        hash |= 0;
    }
    return Math.abs(hash) % buckets;
}

export function getCookie(name: string): string | null {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
}

function deleteCookie(name: string): void {
    if (typeof document === 'undefined') return;
    document.cookie = `${name}=; Path=/; Max-Age=0`;
}

function setCookie(name: string, value: string, maxAgeDays: number): void {
    if (typeof document === 'undefined') return;
    const expires = new Date(Date.now() + maxAgeDays * 86_400_000).toUTCString();
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${name}=${value}; Expires=${expires}; Path=/; SameSite=Lax${secure}`;
}

/** Read attribution without enrolling an ineligible visitor merely to log an event. */
export function getAssignedABVariant<K extends ABExperimentKey>(key: K): ABVariant<K> | undefined {
    return readArm(AB_EXPERIMENTS[key]) as ABVariant<K> | undefined;
}
